@if (form) {
  <form
    #scrollContainer
    [formGroup]="form"
    [boFormOptions]="options"
    (ngSubmit)="onSubmit()">
    @if (employeeData().contact_change_pending) {
      <div class="alert alert-warning">
        <i class="iconoir-warning-circle"></i> Employee contact details verification is pending
      </div>
    }
    <div class="mb-3">
      <app-avatar
        [firstName]="employeeData().first_name"
        [lastName]="employeeData().last_name"
        [size]="'xl'"></app-avatar>
    </div>
    <bo-non-field-errors />
    <app-form-field
      [boFieldPath]="'first_name'"
      [placeholder]="'Enter first name'"></app-form-field>
    <app-form-field
      [boFieldPath]="'last_name'"
      [placeholder]="'Enter last name'"></app-form-field>
    <app-form-field
      boFieldPath="idnum_type"
      placeholder="Select identity type"
      label="Identity type"></app-form-field>
    @if (form.controls['idnum_type'].value === IdNumType.RSAID || !form.controls['idnum_type'].value) {
      <app-id-field
        boFieldPath="idnum"
        placeholder="Enter identity number"
        label="Identity number"></app-id-field>
    }
    @if (form.controls['idnum_type'].value === IdNumType.Passport) {
      <app-form-field
        boFieldPath="idnum"
        placeholder="Enter passport number"
        label="Passport number"></app-form-field>
    }
    @if (!this.employeeId() || !this.invitationData()) {
      <app-form-field
        [boFieldPath]="'date_of_birth'"
        [placeholder]="'Select your date of birth'"
        [label]="'Date of birth'"></app-form-field>
    }
    <app-phone-field
      [boFieldPath]="'phone'"
      [placeholder]="'00 000 000'"
      [label]="'Phone number'"></app-phone-field>
    @if (!employeeData().phone_verified && existingEmployee()) {
      <app-field-unverified-text content="We are waiting on the updated phone number to be verified.">
      </app-field-unverified-text>
    }
    <app-form-field
      [boFieldPath]="'email'"
      [placeholder]="'name@example.com'"
      [label]="'Email address'"></app-form-field>
    @if (!employeeData().email_verified && existingEmployee()) {
      <app-field-unverified-text content="We are waiting on the updated email to be verified.">
      </app-field-unverified-text>
    }
    @if (isEditingEmail || isEditingPhone) {
      <p class="small text-muted mt-neg-1">
        For security reasons, the phone number and email address cannot updated simultaneously.
      </p>
    }
    <div class="form-label">
      User type
      <i
        class="iconoir-info-circle"
        [ngbTooltip]="tipContent"
        placement="bottom"
        tooltipClass="custom-tooltip"></i>
      <ng-template #tipContent>
        <strong>Cardholders</strong> are issued an employee card via the Paystashio app.<br /><br />
        <strong>Admins</strong> manage the dashboard and are not required to have a Paystashio employee card.
      </ng-template>
    </div>
    <div class="mb-3">
      <app-select-field
        label="User type"
        [hideLabel]="true"
        [boFieldPath]="'role'"
        [readonly]="showNoCardsAvailable()"
        [options]="roleOptions$"
        [clearable]="false"
        [closeOnSelect]="true">
      </app-select-field>
    </div>
    @if (showNoCardsAvailable()) {
      <app-field-unverified-text
        content="You have run out of available cards and can only invite Admins."></app-field-unverified-text>
    } @else {
      <div
        class="highlight-issue-card"
        #fadeContainer>
        <div class="form-label">Issue an employee card</div>
        <div class="row form-switch-wrapper align-items-center">
          <div class="col-auto switch-icon pe-1">
            <i
              class="pays-svg-icon ic-card"
              [ngClass]="virtualCardSwitch.value ? 'text-secondary' : 'text-muted'"></i>
          </div>
          <div class="col-auto">
            <div class="form-check form-switch">
              <input
                class="form-check-input"
                type="checkbox"
                aria-label="virtualCardSwitch"
                #virtualCardSwitch
                id="virtualCardSwitch"
                formControlName="create_card" />
              <label
                class="form-check-label"
                for="virtualCardSwitch">
                @if (employeeData().is_card_holder) {
                  Employee card issued
                } @else {
                  Issue an employee card
                }
              </label>
            </div>
          </div>
        </div>
        @if (showApproverFields()) {
          <div class="mb-3">
            <div class="form-label">
              Approvers&nbsp;
              <i
                class="iconoir-info-circle text-muted"
                [ngbTooltip]="approverTooltipText"
                placement="bottom"
                tooltipClass="custom-tooltip"></i>
            </div>
            <app-select-field
              #approversField
              [options]="approverOptions$"
              [multiple]="true"
              [searchable]="true"
              [hideLabel]="true"
              [maxLength]="3"
              [boFieldPath]="'approvers'"
              placeholder="Select between 1 and 3 approvers"></app-select-field>
          </div>
          @if (showRecurringSwitch()) {
            <app-toggle-switch
              (switchChange)="onToggleRecurring($event)"
              [initialValue]="showRecurringTopUp()"
              label="Recurring top-up"
              id="recurringTopUpSwitch"
              [showTooltip]="true"
              [tooltipText]="topupTooltipText"></app-toggle-switch>
          }
          @if (showRecurringTopUp()) {
            <div class="mt-3">
              <app-select-topup-frequency
                (frequencyChange)="handleFrequencyChange($event)"
                [initialValue]="ruleFrequency"></app-select-topup-frequency>
            </div>
            <p class="form-label">What is the target balance for the card?</p>
            <div class="currency-mask">
              <app-form-field
                boFieldPath="topup_amount"
                [addCurrencyMask]="true"
                [currencyMaskOptions]="maskConfig"
                [inputClass]="hasCardLimitError() ? 'is-invalid' : ''"
                label="R0.00"
                [hideLabel]="true"></app-form-field>
            </div>
            <app-card-limit-display
              (hasCardLimitError)="handleCardLimitError($event)"
              [selectedFrequency]="selectedFrequency()"
              [amount]="form.get('topup_amount')?.value"></app-card-limit-display>
          }
        }
      </div>
    }
    <div class="row gy-3 offcanvas-buttons">
      <div class="col-12 col-sm-auto">
        @if (invitationData()) {
          <button
            type="button"
            class="btn btn-outline-dark"
            (click)="onRemoveInvitation(invitationData())"
            title="Remove Invitation">
            Remove
          </button>
        } @else {
          <app-offcanvas-cancel-btn
            [returnUrl]="returnUrl()"
            [returnParams]="returnParams"></app-offcanvas-cancel-btn>
        }
      </div>
      <div class="col-12 col-sm-auto ms-sm-auto">
        <app-submit
          btnID="createEmployeeSubmitBtn"
          [icon]="false"
          [busy]="busy"
          [disabled]="isDisabled()"
          [label]="employeeId() ? 'Update' : 'Add'"></app-submit>
      </div>
    </div>
  </form>
} @else {
  <app-loader></app-loader>
}
